import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useNotify, UserIdentity } from 'react-admin';
import useDebounce from '../../../../hooks/useDebounce';
import {
  ChatsByPhoneAndDate,
  CreateSmsMessagePayload,
  MessageCategory,
  MessageLog,
  MessageLogsByDateAndLastOutboundResponse,
  MessageLogWithLastOutboundMessage,
  MessagePartyType,
  MessageResolutionStatus,
  SimplifiedMemberCommunicationStatus,
  SimplifiedMessageLog,
  TrackingNumberWithAgents,
} from '../../../../interfaces/appointments';
import messagesService from '../../../../services/appointments/messages';
import { AuthService } from '../../../../services/auth';
import coreService from '../../../../services/core';
import { StorageName } from '../../../../util/constants';
import { formatDateString, isToday } from '../../../../util/dateFormat';
import { asE164Number } from '../../../../util/stringFormat';
import '../../communication-center.css';
import { ChatHeader } from '../ChatHeader/ChatHeader';
import ChatMessage from '../ChatMessage/ChatMessage';
import './chat-drawer.css';

const CHARACTER_LIMIT = 1600;
const PHONE_NUMBER_LIMIT = 12;

export interface SimplifiedMember {
  id?: number | null;
  name?: string | null;
  phone: string;
}

export type OpenStatus = { source: 'row' | 'new' | 'none'; open: boolean };

interface Props {
  drawerStatus: OpenStatus;
  loading: boolean;
  trackingNumbers: TrackingNumberWithAgents[];
  allMessages: MessageLogsByDateAndLastOutboundResponse;
  canEditMember?: boolean;
  messages: ChatsByPhoneAndDate;
  memberCommunicationStatus: SimplifiedMemberCommunicationStatus;
  selectedMessage: SimplifiedMessageLog | null;
  onLoadOlderMessages: (
    phone: string,
    systemPhone: string,
    beforeMessageId: number,
  ) => Promise<ChatsByPhoneAndDate | null>;
  onLoadNewerMessages: (
    phone: string,
    systemPhone: string,
    afterMessageId: number,
  ) => Promise<ChatsByPhoneAndDate | null>;
  onSelectMember: (data: { phone: string; memberId: number } | null) => void;
  onClose: () => void;
}

// Helper: initial state for reducer (for better state grouping)
const initialChatState = {
  messagesByPhone: {} as ChatsByPhoneAndDate,
  currentTrackingNumber: 'all',
  newMessage: '',
  draftMessagesByPhone: {} as Record<string, string>,
  members: [] as SimplifiedMember[],
  selectedMember: null as SimplifiedMember | null,
  selectedPhoneNumber: null as string | null,
  hoveredLabel: null as number | null,
  selectedMessage: null as MessageLogWithLastOutboundMessage | null,
  selectedChat: {} as { [date: string]: MessageLogWithLastOutboundMessage[] },
  isSending: false,
  isSearchingMembers: false,
  isLoadingOlder: false,
  isLoadingNewer: false,
  hasMoreOlder: true,
  hasMoreNewer: false,
};

type ChatAction =
  | { type: 'RESET_CHAT' }
  | { type: 'SET_MESSAGES_BY_PHONE'; payload: ChatsByPhoneAndDate }
  | { type: 'SET_CURRENT_TRACKING_NUMBER'; payload: string }
  | { type: 'SET_NEW_MESSAGE'; payload: string }
  | { type: 'SET_DRAFT_MESSAGES_BY_PHONE'; payload: Record<string, string> }
  | { type: 'SET_MEMBERS'; payload: SimplifiedMember[] }
  | { type: 'SET_SELECTED_MEMBER'; payload: SimplifiedMember | null }
  | { type: 'SET_SELECTED_PHONE_NUMBER'; payload: string | null }
  | { type: 'SET_HOVERED_LABEL'; payload: number | null }
  | { type: 'SET_SELECTED_MESSAGE'; payload: MessageLogWithLastOutboundMessage | null }
  | { type: 'SET_SELECTED_CHAT'; payload: { [date: string]: MessageLogWithLastOutboundMessage[] } }
  | { type: 'SET_IS_SENDING'; payload: boolean }
  | { type: 'SET_IS_SEARCHING_MEMBERS'; payload: boolean }
  | { type: 'SET_IS_LOADING_OLDER'; payload: boolean }
  | { type: 'SET_IS_LOADING_NEWER'; payload: boolean }
  | { type: 'SET_HAS_MORE_OLDER'; payload: boolean }
  | { type: 'SET_HAS_MORE_NEWER'; payload: boolean }
  | { type: 'PREPEND_MESSAGES'; payload: ChatsByPhoneAndDate }
  | { type: 'APPEND_MESSAGES'; payload: ChatsByPhoneAndDate };

function chatReducer(state: typeof initialChatState, action: ChatAction) {
  switch (action.type) {
    case 'RESET_CHAT':
      return { ...initialChatState };
    case 'SET_MESSAGES_BY_PHONE':
      return { ...state, messagesByPhone: action.payload };
    case 'SET_CURRENT_TRACKING_NUMBER':
      return { ...state, currentTrackingNumber: action.payload };
    case 'SET_NEW_MESSAGE':
      return { ...state, newMessage: action.payload };
    case 'SET_DRAFT_MESSAGES_BY_PHONE':
      return { ...state, draftMessagesByPhone: action.payload };
    case 'SET_MEMBERS':
      return { ...state, members: action.payload };
    case 'SET_SELECTED_MEMBER':
      return {
        ...state,
        selectedMember: action.payload,
        selectedPhoneNumber: action.payload?.phone || null,
      };
    case 'SET_SELECTED_PHONE_NUMBER':
      return {
        ...state,
        selectedPhoneNumber: action.payload,
      };
    case 'SET_HOVERED_LABEL':
      return { ...state, hoveredLabel: action.payload };
    case 'SET_SELECTED_MESSAGE':
      return { ...state, selectedMessage: action.payload };
    case 'SET_SELECTED_CHAT':
      return { ...state, selectedChat: action.payload };
    case 'SET_IS_SENDING':
      return { ...state, isSending: action.payload };
    case 'SET_IS_SEARCHING_MEMBERS':
      return { ...state, isSearchingMembers: action.payload };
    case 'SET_IS_LOADING_OLDER':
      return { ...state, isLoadingOlder: action.payload };
    case 'SET_IS_LOADING_NEWER':
      return { ...state, isLoadingNewer: action.payload };
    case 'SET_HAS_MORE_OLDER':
      return { ...state, hasMoreOlder: action.payload };
    case 'SET_HAS_MORE_NEWER':
      return { ...state, hasMoreNewer: action.payload };
    case 'PREPEND_MESSAGES':
      return {
        ...state,
        messagesByPhone: Object.keys(action.payload).reduce((acc, phone) => {
          const existingDates = state.messagesByPhone[phone] || {};
          const newDates = action.payload[phone] || {};

          acc[phone] = Object.keys({ ...newDates, ...existingDates }).reduce((dateAcc, date) => {
            const existingMessages = existingDates[date] || [];
            const newMessages = newDates[date] || [];

            const allMessages = [...newMessages, ...existingMessages];
            const uniqueMessages = Array.from(
              new Map(allMessages.map((msg) => [msg.id, msg])).values(),
            ).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

            dateAcc[date] = uniqueMessages;
            return dateAcc;
          }, {} as typeof existingDates);

          return acc;
        }, {} as ChatsByPhoneAndDate),
      };
    case 'APPEND_MESSAGES':
      return {
        ...state,
        messagesByPhone: Object.keys(action.payload).reduce((acc, phone) => {
          const existingDates = state.messagesByPhone[phone] || {};
          const newDates = action.payload[phone] || {};

          acc[phone] = Object.keys({ ...existingDates, ...newDates }).reduce((dateAcc, date) => {
            const existingMessages = existingDates[date] || [];
            const newMessages = newDates[date] || [];

            const allMessages = [...existingMessages, ...newMessages];
            const uniqueMessages = Array.from(
              new Map(allMessages.map((msg) => [msg.id, msg])).values(),
            ).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

            dateAcc[date] = uniqueMessages;
            return dateAcc;
          }, {} as typeof existingDates);

          return acc;
        }, {} as ChatsByPhoneAndDate),
      };
    default:
      return state;
  }
}

const ChatDrawer: React.FC<Props> = ({
  drawerStatus,
  allMessages,
  trackingNumbers = [],
  messages: initialMessages,
  memberCommunicationStatus,
  canEditMember = true,
  loading,
  selectedMessage: initialSelectedMessage,
  onLoadOlderMessages,
  onLoadNewerMessages,
  onClose,
  onSelectMember,
}) => {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);

  const {
    messagesByPhone,
    currentTrackingNumber,
    newMessage,
    draftMessagesByPhone,
    members,
    selectedMember,
    selectedPhoneNumber,
    hoveredLabel,
    selectedMessage,
    selectedChat,
    isSending,
    isSearchingMembers,
    isLoadingOlder,
    isLoadingNewer,
    hasMoreOlder,
    hasMoreNewer,
  } = state;

  const notify = useNotify();
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const smsOptedOut = memberCommunicationStatus.smsOptedOut || false;

  // Memoize trackingNumberObj for performance
  const trackingNumberObj = useMemo(
    () => trackingNumbers.find((n) => n.trackingNumber === currentTrackingNumber) || null,
    [trackingNumbers, currentTrackingNumber],
  );

  // Handlers
  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newTab: string) => {
      dispatch({ type: 'SET_CURRENT_TRACKING_NUMBER', payload: newTab });

      if (newTab === 'all') {
        dispatch({ type: 'SET_SELECTED_CHAT', payload: allMessages });
        return;
      }

      dispatch({
        type: 'SET_NEW_MESSAGE',
        payload: draftMessagesByPhone[newTab] ?? '',
      });

      const chatMessages = messagesByPhone[newTab] || initialMessages[newTab] || {};
      dispatch({ type: 'SET_SELECTED_CHAT', payload: chatMessages });
    },
    [allMessages, draftMessagesByPhone, messagesByPhone, initialMessages],
  );

  const handleSend = useCallback(async () => {
    const trimmedMessage = newMessage.trim();
    if (!trimmedMessage || !selectedPhoneNumber || !currentTrackingNumber) {
      return;
    }

    const localProfile = localStorage.getItem(StorageName.PROFILE);
    const providerId = localProfile ? (JSON.parse(localProfile) as UserIdentity).id : undefined;

    dispatch({ type: 'SET_IS_SENDING', payload: true });

    const payload: CreateSmsMessagePayload = {
      to: selectedPhoneNumber,
      from: currentTrackingNumber,
      content: trimmedMessage,
      messageCategory: MessageCategory.SmsAgentReply,
    };

    if (selectedMember?.id) {
      payload.coreMemberId = selectedMember.id;
    }

    if (providerId) {
      payload.coreProviderId = Number(providerId);
    }

    const response = await messagesService.sendSmsMessage(payload);

    dispatch({ type: 'SET_IS_SENDING', payload: false });

    if (!response.success) {
      notify('The message could not be sent', 'error', { autoHideDuration: 15 });
      return;
    }

    const lastDate = Object.keys(selectedChat).pop() || new Date().toISOString().split('T')[0];

    const trackingNumberObj = trackingNumbers.find(
      (n) => n.trackingNumber === currentTrackingNumber,
    );

    const newMessageEntry =
      response.data && trackingNumberObj
        ? [{ ...response.data, trackingNumber: trackingNumberObj }]
        : [];

    const updatedMessages = {
      ...selectedChat,
      [lastDate]: [...(selectedChat[lastDate] || []), ...newMessageEntry],
    };

    dispatch({
      type: 'SET_MESSAGES_BY_PHONE',
      payload: {
        ...messagesByPhone,
        [currentTrackingNumber]: {
          ...messagesByPhone[currentTrackingNumber],
          [lastDate]: updatedMessages[lastDate],
        },
      },
    });
    dispatch({ type: 'SET_SELECTED_CHAT', payload: updatedMessages });

    dispatch({ type: 'SET_NEW_MESSAGE', payload: '' });
    dispatch({
      type: 'SET_DRAFT_MESSAGES_BY_PHONE',
      payload: {
        ...draftMessagesByPhone,
        [currentTrackingNumber]: '',
      },
    });
  }, [
    newMessage,
    selectedMember,
    selectedPhoneNumber,
    currentTrackingNumber,
    selectedChat,
    trackingNumbers,
    messagesByPhone,
    draftMessagesByPhone,
    notify,
  ]);

  const handleChatInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const currentInput = e.target.value.slice(0, CHARACTER_LIMIT);

      dispatch({ type: 'SET_NEW_MESSAGE', payload: currentInput });
      dispatch({
        type: 'SET_DRAFT_MESSAGES_BY_PHONE',
        payload: {
          ...draftMessagesByPhone,
          [currentTrackingNumber]: currentInput,
        },
      });
    },
    [draftMessagesByPhone, currentTrackingNumber],
  );

  const handlePhoneNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const currentInput = asE164Number(
        e.target.value.slice(0, PHONE_NUMBER_LIMIT).replaceAll(/[a-z]/gi, ''),
      );
      dispatch({ type: 'SET_SELECTED_PHONE_NUMBER', payload: currentInput });
    },
    [],
  );

  const searchMembersByTerm = useCallback(async (searchTerm?: string | null) => {
    if (!searchTerm) {
      return;
    }
    dispatch({ type: 'SET_IS_SEARCHING_MEMBERS', payload: true });

    const members = (await coreService.fetchMembers({ searchTerm })).data.map((member) => ({
      id: member.id,
      phone: member.phone,
      name: member.fullName,
    }));

    dispatch({ type: 'SET_MEMBERS', payload: members });
    dispatch({ type: 'SET_IS_SEARCHING_MEMBERS', payload: false });
  }, []);

  const handleResolveMessage = useCallback(
    async (date: string, messageId: number, newStatus: MessageResolutionStatus) => {
      const localProfile = AuthService.getLocalProfile();

      const response = await messagesService.updateMessageResolutionStatus(messageId, {
        resolutionStatus: newStatus,
        resolutionByCoreProviderId: Number(localProfile.id),
        resolutionByCoreProviderFullname: localProfile.fullName,
      });

      if (!response.success) {
        notify('The message could not be resolved', 'error', { autoHideDuration: 5 });
        return;
      }

      const updatedMessages = {
        ...selectedChat,
        [date]: selectedChat[date].map((message) =>
          message.id === messageId ? { ...message, ...response.data } : message,
        ),
      };

      dispatch({
        type: 'SET_MESSAGES_BY_PHONE',
        payload: {
          ...messagesByPhone,
          [currentTrackingNumber]: {
            ...messagesByPhone[currentTrackingNumber],
            ...updatedMessages,
          },
        },
      });

      dispatch({ type: 'SET_SELECTED_CHAT', payload: updatedMessages });
    },
    [selectedChat, messagesByPhone, currentTrackingNumber, notify],
  );

  const renderResolutionChip = useCallback(
    (date: string, message: MessageLog) => {
      if (message.resolutionStatus === MessageResolutionStatus.Pending) {
        return (
          <Box
            justifyContent='center'
            alignItems='center'
            onMouseEnter={() => dispatch({ type: 'SET_HOVERED_LABEL', payload: message.id })}
            onMouseLeave={() => dispatch({ type: 'SET_HOVERED_LABEL', payload: null })}>
            <Button
              size='small'
              color='warning'
              variant='outlined'
              className='resolution-button'
              onClick={() =>
                handleResolveMessage(date, message.id, MessageResolutionStatus.ManuallyResolved)
              }
              sx={{ fontSize: '0.8rem', textTransform: 'none' }}>
              {hoveredLabel === message.id ? 'Set as resolved' : ''}
            </Button>
          </Box>
        );
      } else if (message.resolutionStatus === MessageResolutionStatus.ManuallyResolved) {
        return (
          <Box
            justifyContent='center'
            alignItems='center'
            onMouseEnter={() => dispatch({ type: 'SET_HOVERED_LABEL', payload: message.id })}
            onMouseLeave={() => dispatch({ type: 'SET_HOVERED_LABEL', payload: null })}>
            <Button
              size='small'
              color='success'
              variant='outlined'
              className='undo-resolution-button'
              onClick={() =>
                handleResolveMessage(date, message.id, MessageResolutionStatus.Pending)
              }
              sx={{ fontSize: '0.8rem', textTransform: 'none' }}>
              {hoveredLabel === message.id ? (
                <>
                  <UndoIcon className='undo-icon' />
                  Back to pending
                </>
              ) : (
                ''
              )}
            </Button>
          </Box>
        );
      }
      return null;
    },
    [handleResolveMessage, hoveredLabel],
  );

  const debouncedFetchMembers = useDebounce(searchMembersByTerm);

  const handleResetChat = useCallback(() => {
    dispatch({ type: 'RESET_CHAT' });
  }, []);

  const setInitialChat = useCallback(
    (systemPhone: string, messageId: number) => {
      const messages = initialMessages[systemPhone] || {};

      const selectedMessage =
        Object.values(messages)
          .flat()
          .find((msg) => msg.id === messageId) || null;

      dispatch({ type: 'SET_SELECTED_MESSAGE', payload: selectedMessage });
      dispatch({ type: 'SET_SELECTED_CHAT', payload: messages });
      dispatch({ type: 'SET_CURRENT_TRACKING_NUMBER', payload: systemPhone });
    },
    [initialMessages],
  );

  const handleLoadOlderMessages = useCallback(async () => {
    if (
      isLoadingOlder ||
      !hasMoreOlder ||
      currentTrackingNumber === 'all' ||
      !selectedPhoneNumber
    ) {
      return;
    }

    const dates = Object.keys(selectedChat).sort();
    if (dates.length === 0) {
      return;
    }

    const oldestDate = dates[0];
    const messagesOnOldestDate = selectedChat[oldestDate];
    if (!messagesOnOldestDate || messagesOnOldestDate.length === 0) {
      return;
    }

    const oldestMessage = messagesOnOldestDate[0];

    dispatch({ type: 'SET_IS_LOADING_OLDER', payload: true });

    const olderMessages = await onLoadOlderMessages(
      selectedPhoneNumber,
      currentTrackingNumber,
      oldestMessage.id,
    );

    if (olderMessages && Object.keys(olderMessages).length > 0) {
      dispatch({ type: 'PREPEND_MESSAGES', payload: olderMessages });

      const updatedChat = { ...selectedChat };
      Object.keys(olderMessages).forEach((phone) => {
        Object.keys(olderMessages[phone]).forEach((date) => {
          if (updatedChat[date]) {
            updatedChat[date] = [...olderMessages[phone][date], ...updatedChat[date]];
          } else {
            updatedChat[date] = olderMessages[phone][date];
          }
        });
      });
      dispatch({ type: 'SET_SELECTED_CHAT', payload: updatedChat });
    } else {
      dispatch({ type: 'SET_HAS_MORE_OLDER', payload: false });
    }

    dispatch({ type: 'SET_IS_LOADING_OLDER', payload: false });
  }, [
    isLoadingOlder,
    hasMoreOlder,
    currentTrackingNumber,
    selectedPhoneNumber,
    selectedChat,
    onLoadOlderMessages,
  ]);

  const handleLoadNewerMessages = useCallback(async () => {
    if (
      isLoadingNewer ||
      !hasMoreNewer ||
      currentTrackingNumber === 'all' ||
      !selectedPhoneNumber
    ) {
      return;
    }

    const dates = Object.keys(selectedChat).sort();
    if (dates.length === 0) {
      return;
    }

    const newestDate = dates[dates.length - 1];
    const messagesOnNewestDate = selectedChat[newestDate];
    if (!messagesOnNewestDate || messagesOnNewestDate.length === 0) {
      return;
    }

    const newestMessage = messagesOnNewestDate[messagesOnNewestDate.length - 1];

    dispatch({ type: 'SET_IS_LOADING_NEWER', payload: true });

    const newerMessages = await onLoadNewerMessages(
      selectedPhoneNumber,
      currentTrackingNumber,
      newestMessage.id,
    );

    if (newerMessages && Object.keys(newerMessages).length > 0) {
      dispatch({ type: 'APPEND_MESSAGES', payload: newerMessages });

      const updatedChat = { ...selectedChat };
      Object.keys(newerMessages).forEach((phone) => {
        Object.keys(newerMessages[phone]).forEach((date) => {
          if (updatedChat[date]) {
            updatedChat[date] = [...updatedChat[date], ...newerMessages[phone][date]];
          } else {
            updatedChat[date] = newerMessages[phone][date];
          }
        });
      });
      dispatch({ type: 'SET_SELECTED_CHAT', payload: updatedChat });
    } else {
      dispatch({ type: 'SET_HAS_MORE_NEWER', payload: false });
    }

    dispatch({ type: 'SET_IS_LOADING_NEWER', payload: false });
  }, [
    isLoadingNewer,
    hasMoreNewer,
    currentTrackingNumber,
    selectedPhoneNumber,
    selectedChat,
    onLoadNewerMessages,
  ]);

  // Reset chat when drawer opens
  useEffect(() => {
    if (drawerStatus.open) {
      handleResetChat();
    }
  }, [drawerStatus.open]);

  // Scroll to last message when selectedChat changes
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    scrollTimeout = setTimeout(() => {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView();
      } else if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }
    }, 150);
    return () => clearTimeout(scrollTimeout);
  }, [selectedChat]);

  // Scroll to selected message if set
  useEffect(() => {
    if (selectedMessage) {
      const element = document.querySelector(
        `[reference-message-id="${currentTrackingNumber}-${selectedMessage.id}"]`,
      );
      if (element) {
        (element as HTMLElement).scrollIntoView();
      }
    }
  }, [selectedMessage, currentTrackingNumber]);

  // Set initial chat and member when drawer opens or selection changes
  useEffect(() => {
    if (trackingNumbers.length === 0 || Object.keys(allMessages).length === 0) {
      return;
    }

    if (!initialSelectedMessage) {
      const message = Object.values(allMessages).flat().pop();

      const systemPhone = message?.fromType === MessagePartyType.USER ? message?.to : message?.from;

      if (systemPhone && message?.id) {
        setInitialChat(systemPhone, message.id);
      }
      return;
    }

    const isFromUser = initialSelectedMessage.from.fromType === MessagePartyType.USER;
    const user = isFromUser ? initialSelectedMessage.from : initialSelectedMessage.to;
    const system = isFromUser ? initialSelectedMessage.to : initialSelectedMessage.from;

    if (!!user?.user?.coreMemberId) {
      dispatch({
        type: 'SET_SELECTED_MEMBER',
        payload: {
          id: user?.user?.coreMemberId,
          name: user?.user?.coreMemberFullname,
          phone: user.phoneNumber,
        },
      });
    } else if (drawerStatus.source === 'row') {
      dispatch({
        type: 'SET_SELECTED_PHONE_NUMBER',
        payload: user.phoneNumber || '',
      });
    }

    setInitialChat(system.phoneNumber, initialSelectedMessage.id);
  }, [
    initialSelectedMessage,
    initialMessages,
    trackingNumbers,
    setInitialChat,
    allMessages,
    drawerStatus.source,
  ]);

  // Detect infinite scroll, when scroll up and down
  useEffect(() => {
    const chatContainer = chatRef.current;
    if (!chatContainer || currentTrackingNumber === 'all') {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;

      if (scrollTop < 100 && hasMoreOlder && !isLoadingOlder) {
        handleLoadOlderMessages();
      }

      if (scrollHeight - scrollTop - clientHeight < 100 && hasMoreNewer && !isLoadingNewer) {
        handleLoadNewerMessages();
      }
    };

    chatContainer.addEventListener('scroll', handleScroll);
    return () => chatContainer.removeEventListener('scroll', handleScroll);
  }, [
    currentTrackingNumber,
    hasMoreOlder,
    hasMoreNewer,
    isLoadingOlder,
    isLoadingNewer,
    handleLoadOlderMessages,
    handleLoadNewerMessages,
  ]);

  // Early return if not open
  if (!drawerStatus.open) {
    return null;
  }

  return (
    <Drawer
      anchor='right'
      open={drawerStatus.open}
      onClose={() => {
        handleResetChat();
        onClose();
      }}>
      <Box className='chat-drawer'>
        <ChatHeader
          members={members}
          initialMessages={initialMessages}
          selectedMember={selectedMember}
          isSearchingMembers={isSearchingMembers}
          smsOptedOut={smsOptedOut}
          canEditMember={canEditMember}
          trackingNumbers={trackingNumbers}
          currentTrackingNumber={currentTrackingNumber}
          loading={loading}
          onMemberChange={(member) => {
            if (!member) {
              handleResetChat();
              onSelectMember(null);
              return;
            }
            dispatch({ type: 'SET_SELECTED_MEMBER', payload: member });
            onSelectMember({ phone: member?.phone || '', memberId: member?.id! });
          }}
          onSearch={debouncedFetchMembers}
          onTabChange={handleTabChange}
          onClose={() => {
            handleResetChat();
            onClose();
          }}
        />

        <Box className='chat-content' ref={chatRef}>
          {loading ? (
            <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
              <LinearProgress sx={{ width: '50%' }} />
            </Box>
          ) : Object.keys(selectedChat).length > 0 ? (
            <>
              {isLoadingOlder && (
                <Box display='flex' justifyContent='center' py={2}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {Object.entries(selectedChat).map(([date, messages]) => (
                <Box key={date} className='communication-date-group'>
                  <Box className='message-date-header'>
                    <Typography variant='overline'>
                      {isToday(new Date(date)) ? 'Today' : formatDateString(date, 'MMM D, YYYY')}
                    </Typography>
                  </Box>
                  {messages.map((message) => (
                    <ChatMessage
                      key={message.id}
                      trackingNumbers={trackingNumbers}
                      currentTrackingNumber={trackingNumberObj}
                      message={message}
                      date={date}
                      isSelected={selectedMessage?.id === message.id}
                      renderResolutionChip={renderResolutionChip}
                      messageRef={lastMessageRef}
                    />
                  ))}
                </Box>
              ))}
              {isLoadingNewer && (
                <Box display='flex' justifyContent='center' py={2}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </>
          ) : (
            <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
              <Typography color='#606060'>No messages. Start a new conversation.</Typography>
            </Box>
          )}
        </Box>

        <Divider />

        <Box id='chat-send-messages-box'>
          <Box
            id='chat-target-number-box'
            display='flex'
            flexDirection='row'
            alignItems='center'
            color={currentTrackingNumber === 'all' ? '#a8a8a8' : '#389FBA'}
            width='100%'>
            <Typography>
              To:
              {drawerStatus.source === 'row' &&
                (selectedPhoneNumber ? ` ${selectedPhoneNumber}` : '')}
            </Typography>

            {drawerStatus.source === 'new' && (
              <TextField
                sx={{ width: '100%' }}
                size='small'
                placeholder='Type a phone number or pick a member...'
                value={selectedPhoneNumber}
                disabled={loading || currentTrackingNumber === 'all'}
                onChange={handlePhoneNumberChange}
                maxRows={1}
              />
            )}
          </Box>

          <Box
            display='flex'
            flexDirection='row'
            alignItems='flex-start'
            justifyContent='space-between'
            width='100%'
            gap={1}>
            <Box display='flex' flexDirection='column' id='chat-send-messages-box-group'>
              <TextField
                id='chat-send-messages-box-input'
                fullWidth
                size='small'
                placeholder='Type a message...'
                value={newMessage}
                disabled={
                  loading ||
                  (selectedMember && smsOptedOut) ||
                  !selectedPhoneNumber ||
                  currentTrackingNumber === 'all'
                }
                onChange={handleChatInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                multiline
                maxRows={4}
                sx={{ borderRadius: '16px' }}
              />

              <Typography
                color={
                  newMessage.length === CHARACTER_LIMIT ? 'tomato' : 'gray'
                }>{`${newMessage.length}/${CHARACTER_LIMIT}`}</Typography>
            </Box>{' '}
            <Button
              id='chat-send-message-button'
              className={
                loading ||
                (selectedMember && smsOptedOut) ||
                !selectedPhoneNumber ||
                isSending ||
                currentTrackingNumber === 'all' ||
                newMessage.length === 0
                  ? 'disabled'
                  : ''
              }
              variant='contained'
              onClick={handleSend}
              endIcon={
                isSending ? (
                  <CircularProgress
                    size={15}
                    sx={{ width: '15px', height: '15px' }}
                    color='inherit'
                  />
                ) : (
                  <SendIcon sx={{ width: '15px', height: '15px' }} />
                )
              }
              disabled={
                loading ||
                (selectedMember && smsOptedOut) ||
                !selectedPhoneNumber ||
                isSending ||
                currentTrackingNumber === 'all' ||
                newMessage.length === 0
              }>
              Send
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ChatDrawer;
